
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { PenLine, MapPin, User, ArrowRight, Compass, Globe, Tag } from 'lucide-react';
import { AddBlogPostDialog } from './AddBlogPostDialog';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { GUIDE_TYPES } from './constants/guideTypes';

interface BlogPostCardProps {
  post: {
    id: string;
    title: string;
    content: string;
    created_at: string;
    user_id: string;
    city?: string;
    country?: string;
    countryName?: string;
    guide_type?: string;
    tags?: string[];
    author?: {
      username: string;
      avatar_url: string;
    };
  };
  isOwnPost: boolean;
}

const MAX_CONTENT_LENGTH = 300;

export const BlogPostCard = ({ post, isOwnPost }: BlogPostCardProps) => {
  const contentPreview = post.content.length > MAX_CONTENT_LENGTH 
    ? post.content.substring(0, MAX_CONTENT_LENGTH) + '...'
    : post.content;

  const guideTypeLabel = GUIDE_TYPES.find(type => type.value === post.guide_type)?.label;

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
      <CardHeader className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 opacity-50" />
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <CardTitle className="font-montserrat text-2xl text-primary bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-pink-500">
                {post.title}
              </CardTitle>
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                {post.city && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    <p>{post.city}</p>
                  </div>
                )}
                {post.countryName && (
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    <p>{post.countryName}</p>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Compass className="h-4 w-4" />
                  <p>{formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}</p>
                </div>
                {post.author && (
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <p>{post.author.username}</p>
                  </div>
                )}
              </div>
            </div>
            {isOwnPost && (
              <AddBlogPostDialog post={post}>
                <Button variant="ghost" size="sm" className="gap-2">
                  <PenLine className="h-4 w-4" />
                  Edit
                </Button>
              </AddBlogPostDialog>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {guideTypeLabel && (
              <Badge variant="secondary">
                {guideTypeLabel}
              </Badge>
            )}
            {post.tags && post.tags.length > 0 && (
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-muted-foreground" />
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <div 
          className="prose prose-lg max-w-none dark:prose-invert mb-4 prose-p:text-gray-600 dark:prose-p:text-gray-300"
          dangerouslySetInnerHTML={{ __html: contentPreview }}
        />
        <div className="flex justify-end">
          <Link to={`/blog/${post.id}`}>
            <Button variant="ghost" className="gap-2 hover:text-primary transition-colors">
              Read more <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};
